import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Divider,
} from '@mui/material';
import { ibkrService } from '../../services/ibkrService';

interface Trade {
  id: string;
  date: string;
  symbol: string;
  strategy: string;
  pnl: number;
  duration_days: number;
  reason: string;
}

interface HistoryData {
  trades: Trade[];
  total_pnl?: number;
  win_rate?: number;
  avg_trade?: number;
}

type SortKey = 'date' | 'symbol' | 'strategy' | 'pnl' | 'duration_days' | 'reason';
type SortDir = 'asc' | 'desc';

const IBKRHistory: React.FC = () => {
  const [history, setHistory] = useState<HistoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const fetchHistory = useCallback(async () => {
    try {
      setError(null);
      const data = await ibkrService.getHistory();
      setHistory(data);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der History');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortedTrades = React.useMemo(() => {
    const trades = [...(history?.trades || [])];
    trades.sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const aStr = String(aVal);
      const bStr = String(bVal);
      return sortDir === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
    return trades;
  }, [history?.trades, sortKey, sortDir]);

  const formatCurrency = (val: number) =>
    `$${val.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const columns: { key: SortKey; label: string }[] = [
    { key: 'date', label: 'Datum' },
    { key: 'symbol', label: 'Symbol' },
    { key: 'strategy', label: 'Strategie' },
    { key: 'pnl', label: 'P&L' },
    { key: 'duration_days', label: 'Dauer' },
    { key: 'reason', label: 'Grund' },
  ];

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Trade-History
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {sortedTrades.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Noch keine geschlossenen Trades</Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  {columns.map((col) => (
                    <TableCell key={col.key}>
                      <TableSortLabel
                        active={sortKey === col.key}
                        direction={sortKey === col.key ? sortDir : 'asc'}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedTrades.map((trade, idx) => (
                  <TableRow key={trade.id || idx} hover>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{trade.date}</TableCell>
                    <TableCell>{trade.symbol}</TableCell>
                    <TableCell>{trade.strategy}</TableCell>
                    <TableCell>
                      <Typography
                        fontWeight={600}
                        color={trade.pnl >= 0 ? 'success.main' : 'error.main'}
                      >
                        {trade.pnl >= 0 ? '+' : ''}{formatCurrency(trade.pnl)}
                      </Typography>
                    </TableCell>
                    <TableCell>{trade.duration_days}d</TableCell>
                    <TableCell>{trade.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Summary */}
          <Paper sx={{ p: 2, mt: 2, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Gesamt P&L</Typography>
              <Typography
                fontWeight={700}
                color={(history?.total_pnl ?? 0) >= 0 ? 'success.main' : 'error.main'}
              >
                {(history?.total_pnl ?? 0) >= 0 ? '+' : ''}{formatCurrency(history?.total_pnl ?? 0)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Gewinnquote</Typography>
              <Typography fontWeight={700}>
                {history?.win_rate != null ? `${(history.win_rate ?? 0).toFixed(0)}%` : '--'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Ø Trade</Typography>
              <Typography
                fontWeight={700}
                color={(history?.avg_trade ?? 0) >= 0 ? 'success.main' : 'error.main'}
              >
                {(history?.avg_trade ?? 0) >= 0 ? '+' : ''}{formatCurrency(history?.avg_trade ?? 0)}
              </Typography>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default IBKRHistory;
